import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getStats } from "../../lib/api";
import { Icon } from "../../components/icons";
import type { Stats } from "../../lib/types";

interface StatTile {
  h: string;
  v: string | number;
  d?: string;
  up?: boolean;
  dn?: boolean;
  spark: number[];
}

function spark(values: number[]): JSX.Element {
  const max = Math.max(...values, 1);
  return (
    <div className="stat-spark">
      {values.map((v, j) => (
        <i
          key={j}
          style={{
            height: `${(v / max) * 100}%`,
            opacity: 0.4 + (j / values.length) * 0.6,
          }}
        />
      ))}
    </div>
  );
}

export function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const refresh = () => {
    setLoading(true);
    getStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const tiles: StatTile[] = stats
    ? [
        {
          h: "Users",
          v: stats.totalUsers ?? 0,
          d: "registered",
          spark: [
            Math.max(1, (stats.totalUsers ?? 0) - 6),
            Math.max(1, (stats.totalUsers ?? 0) - 5),
            Math.max(1, (stats.totalUsers ?? 0) - 4),
            Math.max(1, (stats.totalUsers ?? 0) - 3),
            Math.max(1, (stats.totalUsers ?? 0) - 2),
            Math.max(1, (stats.totalUsers ?? 0) - 1),
            stats.totalUsers ?? 0,
          ],
        },
        {
          h: "Samples",
          v: stats.totalJobs ?? 0,
          d: "all-time",
          up: true,
          spark: Array.from({ length: 7 }, (_, i) => Math.max(1, ((stats.totalJobs ?? 0) * (i + 1)) / 7)),
        },
        {
          h: "Completed",
          v: stats.completedJobs ?? 0,
          d: stats.totalJobs
            ? `${Math.round(((stats.completedJobs ?? 0) / stats.totalJobs) * 100)}% rate`
            : "—",
          up: true,
          spark: Array.from({ length: 7 }, (_, i) => Math.max(1, ((stats.completedJobs ?? 0) * (i + 1)) / 7)),
        },
        {
          h: "Failed",
          v: stats.failedJobs ?? 0,
          d: stats.totalJobs
            ? `${Math.round(((stats.failedJobs ?? 0) / stats.totalJobs) * 100)}% rate`
            : "—",
          dn: (stats.failedJobs ?? 0) > 0,
          spark: Array.from({ length: 7 }, (_, i) => Math.max(1, ((stats.failedJobs ?? 0) * (i + 1)) / 7)),
        },
        {
          h: "Active now",
          v: stats.activeJobs ?? 0,
          d: stats.activeJobs ? "in flight" : "idle",
          spark: Array.from({ length: 7 }, () => Math.max(1, Math.random() * 5)),
        },
        {
          h: "GPU spend",
          v: `$${stats.gpuCost.total?.toFixed(2) ?? "0.00"}`,
          d: `$${stats.gpuCost.avgPerJob?.toFixed(4) ?? "0.0000"} avg`,
          up: true,
          spark: [
            stats.gpuCost.today,
            stats.gpuCost.week / 7,
            stats.gpuCost.week / 4,
            stats.gpuCost.week / 2,
            stats.gpuCost.month / 4,
            stats.gpuCost.month / 2,
            stats.gpuCost.month,
          ].map((v) => Math.max(0.01, v)),
        },
        {
          h: "TTS",
          v: stats.ttsJobs ?? 0,
          d: stats.totalJobs
            ? `${Math.round(((stats.ttsJobs ?? 0) / stats.totalJobs) * 100)}% of total`
            : "—",
          spark: Array.from({ length: 7 }, (_, i) => Math.max(1, ((stats.ttsJobs ?? 0) * (i + 1)) / 7)),
        },
        {
          h: "Conversion",
          v: stats.vcJobs ?? 0,
          d: stats.totalJobs
            ? `${Math.round(((stats.vcJobs ?? 0) / stats.totalJobs) * 100)}% of total`
            : "—",
          spark: Array.from({ length: 7 }, (_, i) => Math.max(1, ((stats.vcJobs ?? 0) * (i + 1)) / 7)),
        },
      ]
    : [];

  return (
    <div className="content">
      <div className="ph">
        <div>
          <div className="ph-eyebrow">Admin</div>
          <h1 className="ph-title">Workspace overview</h1>
          <div className="ph-lede">
            Inference, cost, and user activity across all environments. Refresh anytime.
          </div>
        </div>
        <div className="ph-actions">
          <button type="button" className="btn btn-ghost" onClick={refresh}>
            <Icon.Refresh /> Refresh
          </button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="stat-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="skeleton-card" style={{ height: 130 }} />
          ))}
        </div>
      ) : (
        <>
          <div className="stat-grid">
            {tiles.map((s) => (
              <div key={s.h} className="stat">
                <div className="stat-h">{s.h}</div>
                <div className="stat-v">{s.v}</div>
                {s.d && (
                  <div className={`stat-d ${s.up ? "up" : ""} ${s.dn ? "dn" : ""}`}>
                    {s.up ? "↑ " : s.dn ? "↓ " : ""}
                    {s.d}
                  </div>
                )}
                {spark(s.spark)}
              </div>
            ))}
          </div>

          {stats && (
            <div className="gpu-block">
              <div className="gpu-h">
                <span>GPU cost breakdown</span>
                <span className="meta">
                  {Math.round(stats.totalInferenceSeconds)}s on H100 @ $0.00418/s
                </span>
              </div>
              {[
                { k: "Today", v: stats.gpuCost.today },
                { k: "This week", v: stats.gpuCost.week },
                { k: "This month", v: stats.gpuCost.month },
                { k: "All time", v: stats.gpuCost.total },
                { k: "Avg per sample", v: stats.gpuCost.avgPerJob },
              ].map(({ k, v }) => {
                const pct = stats.gpuCost.total
                  ? Math.min(100, (v / stats.gpuCost.total) * 100)
                  : 0;
                return (
                  <div key={k} className="gpu-row">
                    <span className="label">{k}</span>
                    <div className="gpu-bar">
                      <i style={{ width: pct + "%" }} />
                    </div>
                    <span className="gpu-val">${v.toFixed(4)}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="quick-actions">
            <Link to="/admin/users" className="qa-card" style={{ textDecoration: "none" }}>
              <span className="qa-icon"><Icon.Users /></span>
              <span className="qa-body">
                <span className="qa-t">Manage users</span>
                <span className="qa-d">{stats?.totalUsers ?? 0} accounts</span>
              </span>
              <span className="qa-arrow"><Icon.Arrow /></span>
            </Link>
            <Link to="/admin/jobs" className="qa-card" style={{ textDecoration: "none" }}>
              <span className="qa-icon"><Icon.Music /></span>
              <span className="qa-body">
                <span className="qa-t">All samples</span>
                <span className="qa-d">{stats?.totalJobs ?? 0} across the workspace</span>
              </span>
              <span className="qa-arrow"><Icon.Arrow /></span>
            </Link>
            <Link to="/admin/voices" className="qa-card" style={{ textDecoration: "none" }}>
              <span className="qa-icon"><Icon.Library /></span>
              <span className="qa-body">
                <span className="qa-t">Voice references</span>
                <span className="qa-d">manage curated voices</span>
              </span>
              <span className="qa-arrow"><Icon.Arrow /></span>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
